import { create } from "zustand";

/**
 * Latest snapshot per curve, as streamed on the `curves` WS channel
 * (pks-notifications `query_curve_snapshots`: one row per curve at its max
 * version, joined with the definition's family/label/status).
 *
 * The Curves desk navigator reads `as_of` and `override_count` from here so
 * health is live without one `/snapshots` request per curve.
 */
export interface CurveSnapshotLive {
  curve_id: string;
  snapshot_id?: string;
  model_type?: string;
  as_of?: string;
  has_overrides?: boolean;
  override_count?: number;
  version_id?: number;
  curve_family?: string;
  label?: string;
  status?: string;
}

interface CurveSnapshotState {
  byId: Map<string, CurveSnapshotLive>;
  setSnapshot: (rows: CurveSnapshotLive[]) => void;
  applyDelta: (upserts: CurveSnapshotLive[], removedIds: string[]) => void;
}

export const useCurveSnapshotStore = create<CurveSnapshotState>((set, get) => ({
  byId: new Map(),
  setSnapshot: (rows) => {
    const m = new Map<string, CurveSnapshotLive>();
    for (const r of rows) m.set(r.curve_id, r);
    set({ byId: m });
  },
  applyDelta: (upserts, removedIds) => {
    const next = new Map(get().byId);
    for (const id of removedIds) next.delete(id);
    for (const r of upserts) next.set(r.curve_id, r);
    set({ byId: next });
  },
}));

/** Parse a `curves` broadcast body: `{ curves: [...], removed?: [...] }`. */
export function parseCurvesWsData(data: unknown): { curves: CurveSnapshotLive[]; removed: string[] } {
  if (!data || typeof data !== "object") return { curves: [], removed: [] };
  const o = data as Record<string, unknown>;
  const curves = Array.isArray(o.curves)
    ? (o.curves as unknown[]).filter(
        (r): r is CurveSnapshotLive =>
          !!r && typeof r === "object" && typeof (r as { curve_id?: unknown }).curve_id === "string",
      )
    : [];
  const removed = Array.isArray(o.removed) ? (o.removed as unknown[]).filter((x): x is string => typeof x === "string") : [];
  return { curves, removed };
}
